import React, { useEffect, useMemo } from 'react';
import {
  Alignment,
  Fit,
  Layout,
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceNumber,
  useViewModelInstanceString,
  FontAsset
} from '@rive-app/react-canvas';
import { decodeFont } from '@rive-app/react-canvas';
import { DEVICE_PIXEL_RATIO, LAUNCHES_PER_GAME } from '../game-engine';
import { useAssets } from './AssetHandler';
import Loader from '~/core/components/Loader';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomBallsRemaining, atomControlsReady, atomGameState, atomIsReplayMode } from '../states/game.atoms';
import { usePlayButton } from '../hooks/usePlayButton';
import { atomUserInput, atomIsIgamingDemo } from '../states';
import { useAccount } from 'wagmi';
import { formatUsdFromEth } from '../utils/demoUsd';

export const PlayButton: React.FC = () => {
  const { address } = useAccount();

  // Atoms
  const gameState = useAtomValue(atomGameState);
  const ballsRemaining = useAtomValue(atomBallsRemaining);
  const setControlsReady = useSetAtom(atomControlsReady);
  const isReplayMode = useAtomValue(atomIsReplayMode);
  const userInput = useAtomValue(atomUserInput);
  const isIgamingDemo = useAtomValue(atomIsIgamingDemo);

  // Charge Controller
  const {
    chargeProgress,
    isPreparing,
    handlePointerPressStart,
    handlePointerPressRelease,
    handlePointerPressCancel
  } = usePlayButton();

  const { assets } = useAssets();
  const bungeeFontBytes = assets?.fonts?.['bungee'];

  const { rive, RiveComponent } = useRive({
    buffer: assets.riveFiles['play-button'],
    artboard: 'play-button',
    stateMachines: 'state',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    autoBind: true,
    assetLoader: (asset, bytes) => {
      if (asset.cdnUuid.length > 0 || bytes.length > 0) {
        return false;
      }
      if (!asset.isFont || !bungeeFontBytes) {
        return false;
      }

      const fontBytes = new Uint8Array(bungeeFontBytes);
      void decodeFont(fontBytes).then(font => {
        (asset as FontAsset).setFont(font);
        font.unref();
      });
      return true;
    },
    onLoad: () => {
      setControlsReady(true);
    },
  }, { customDevicePixelRatio: DEVICE_PIXEL_RATIO });

  const buttonViewModel = useViewModel(rive, { name: 'button' });
  const viewModelInstance = useViewModelInstance(buttonViewModel, { useDefault: true, rive });
  const { setValue: setLabel } = useViewModelInstanceString('label', viewModelInstance);
  const { setValue: setChargeValue } = useViewModelInstanceNumber('charge', viewModelInstance);
  const { setValue: setProgressValue } = useViewModelInstanceNumber('progress', viewModelInstance);

  const normalizedChargeProgress = useMemo(
    () => Math.max(0, Math.min(chargeProgress, 1)),
    [chargeProgress],
  );
  const chargeNormalized = normalizedChargeProgress;
  const chargeValue = useMemo(() => {
    const min = 150;
    const max = 395;
    return max - (max - min) * chargeNormalized;
  }, [chargeNormalized]);

  const remainingNormalized = useMemo(() => {
    const normalized = ballsRemaining / LAUNCHES_PER_GAME;
    return Math.max(0, Math.min(normalized, 1));
  }, [ballsRemaining]);

  const progressValue = useMemo(() => {
    const min = 370;
    const max = 120;
    return min + (max - min) * remainingNormalized;
  }, [remainingNormalized]);

  const buttonLabel = useMemo(() => {
    if (isPreparing) {
      return '';
    }
    switch (gameState) {
      case 'waiting': return 'Start';
      case 'levelup': return 'Next\nLevel';
      case 'maxlevel': return 'Max\nLevel';
      default: return String(ballsRemaining);
    }
  }, [ballsRemaining, gameState, isPreparing]);

  useEffect(() => {
    if (!rive || !viewModelInstance) {
      return;
    }
    setLabel(buttonLabel);
  }, [buttonLabel, rive, setLabel, viewModelInstance]);

  useEffect(() => {
    if (!rive || !viewModelInstance) {
      return;
    }
    setChargeValue(chargeValue);
  }, [chargeValue, rive, setChargeValue, viewModelInstance]);

  useEffect(() => {
    if (!rive || !viewModelInstance) {
      return;
    }
    setProgressValue(progressValue);
  }, [progressValue, rive, setProgressValue, viewModelInstance]);

  useEffect(() => {
    if (!rive) {
      return;
    }
    rive.setupRiveListeners();
    return () => { rive.removeRiveListeners(); };
  }, [rive]);

  return (
    <button
      type="button"
      className="relative text-white focus:outline-none"
      onPointerDown={handlePointerPressStart}
      onPointerUp={handlePointerPressRelease}
      onPointerCancel={handlePointerPressCancel}
    >
      <div className="relative flex items-center justify-center h-[150px] md:h-[270px] aspect-square">
        <RiveComponent className="h-full w-full" />
        {isPreparing && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <Loader size="20%" stroke="#fff" />
          </div>
        )}
        {gameState === 'waiting' && !isPreparing && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="mt-[40px] md:mt-[70px]">
              <p className="text-[10px] md:text-xs font-blinko text-white/70 uppercase">
                {isIgamingDemo
                  ? formatUsdFromEth(userInput.tollAmount)
                  : !address && !isReplayMode ? `Demo Mode` : `${userInput.tollAmount} ETH`}
              </p>
            </div>
          </div>
        )}
      </div>
    </button>
  );
};
